import { useEffect, useMemo, useState } from "react";
import type { MissingField, RecognitionPreview } from "../api.js";

type EditableField = {
  key: string;
  label: string;
  type: "text" | "date" | "enum";
  value: string;
  options?: Array<{ value: string; label: string }>;
};

const decisionOptions = [
  { value: "CONTINUE_REVISION", label: "继续改版" },
  { value: "REQUEST_CONFIRMATION_APPROVAL", label: "申请确认版审批" },
  { value: "REQUEST_PRODUCTION_APPROVAL", label: "申请大货版审批" },
];

function missingFieldType(field: MissingField): "text" | "date" | "enum" {
  if (field.key === "fields.decision") return "enum";
  if (
    field.key === "fields.nextMilestoneDate" ||
    field.key === "fields.receivedAt" ||
    field.key.endsWith("Date")
  ) {
    return "date";
  }
  return "text";
}

function editableFields(draft: Record<string, unknown>): EditableField[] {
  const value = draft.editableFields;
  if (!Array.isArray(value)) return [];
  return value.filter(
    (field): field is EditableField =>
      Boolean(field) &&
      typeof field === "object" &&
      typeof (field as EditableField).key === "string" &&
      typeof (field as EditableField).label === "string" &&
      typeof (field as EditableField).value === "string",
  );
}

export function RecognitionReview({
  taskId,
  draft,
  preview,
  missing,
  confirmationId,
  onReorganize,
  onConfirm,
  onCancel,
}: {
  taskId: string;
  draft: Record<string, unknown>;
  preview: RecognitionPreview;
  missing: MissingField[];
  confirmationId: string | null;
  onReorganize(input: {
    sourceNote: string;
    transcript: string;
    fieldOverrides: Record<string, string | number>;
  }): Promise<void>;
  onConfirm(changes: Record<string, unknown>): Promise<void>;
  onCancel(): Promise<void>;
}) {
  const definitions = useMemo(() => editableFields(draft), [draft]);
  const [sourceNote, setSourceNote] = useState(preview.sourceNote);
  const [transcript, setTranscript] = useState(preview.transcript);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(
    Object.fromEntries(definitions.map((field) => [field.key, field.value])),
  );
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSourceNote(preview.sourceNote);
    setTranscript(preview.transcript);
    setFieldValues(Object.fromEntries(definitions.map((field) => [field.key, field.value])));
    setOverrides({});
  }, [definitions, preview.revision, preview.sourceNote, preview.transcript]);

  const sourceDirty =
    sourceNote !== preview.sourceNote ||
    transcript !== preview.transcript ||
    Object.values(overrides).some((value) => value.trim());
  const businessChanges = Object.fromEntries(
    definitions
      .filter((field) => fieldValues[field.key] !== field.value)
      .map((field) => [field.key, fieldValues[field.key]]),
  );

  async function reorganize() {
    setBusy(true);
    try {
      await onReorganize({
        sourceNote,
        transcript,
        fieldOverrides: overrides,
      });
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    try {
      await onConfirm(businessChanges);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="recognition-review">
      <div className="review-notice">
        <strong>识别结果 · 待您确认</strong>
        <span>尚未写入样品系统</span>
      </div>
      {preview.imageObservations.length ? (
        <section className="review-section">
          <p className="eyebrow">01 · 图片识别</p>
          <div className="recognized-images">
            {preview.imageObservations.map((item) => (
              <figure key={item.resourceId}>
                <img
                  src={"/api/tasks/" + taskId + "/media/" + item.resourceId + "/preview"}
                  alt={item.observation || "样品图片"}
                />
                <figcaption>{item.observation || "未形成可靠图片描述"}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      ) : null}
      <section className="review-section">
        <label htmlFor="review-source-note">02 · 您输入的文字</label>
        <textarea
          id="review-source-note"
          aria-label="您输入的文字"
          value={sourceNote}
          onChange={(event) => setSourceNote(event.target.value)}
        />
      </section>
      <section className="review-section">
        <label htmlFor="review-transcript">03 · 语音转写原文（可以直接改）</label>
        {preview.audioPreviewUrl ? (
          <audio aria-label="原始语音" controls src={preview.audioPreviewUrl} />
        ) : null}
        <textarea
          id="review-transcript"
          aria-label="语音转写原文"
          value={transcript}
          onChange={(event) => setTranscript(event.target.value)}
        />
      </section>
      {missing.length ? (
        <section className="review-section missing-section">
          <p className="eyebrow">需要补充</p>
          {missing.map((field) => {
            if (field.key === "product") {
              return (
                <div className="missing-guidance" key={field.key}>
                  <strong>{field.label}</strong>
                  <small>{field.hint}</small>
                  <p>请在上面的“您输入的文字”中改正款号，再点击“根据修改重新整理”。</p>
                </div>
              );
            }
            const type = missingFieldType(field);
            const update = (value: string) =>
              setOverrides((current) => ({ ...current, [field.key]: value }));
            return (
              <label key={field.key}>
                <span>{field.label}</span>
                <small>{field.hint}</small>
                {type === "enum" ? (
                  <select
                    aria-label={field.label}
                    value={overrides[field.key] ?? ""}
                    onChange={(event) => update(event.target.value)}
                  >
                    <option value="">请选择</option>
                    {decisionOptions.map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={type === "date" ? "date" : "text"}
                    aria-label={field.label}
                    value={overrides[field.key] ?? ""}
                    onChange={(event) => update(event.target.value)}
                  />
                )}
              </label>
            );
          })}
        </section>
      ) : null}
      {definitions.length ? (
        <section className="review-section">
          <p className="eyebrow">04 · Agent 综合整理</p>
          {definitions.map((field) => (
            <label key={field.key}>
              <span>{field.label}</span>
              {field.type === "enum" ? (
                <select
                  aria-label={field.label}
                  value={fieldValues[field.key] ?? ""}
                  onChange={(event) =>
                    setFieldValues((current) => ({
                      ...current,
                      [field.key]: event.target.value,
                    }))
                  }
                >
                  {(field.options ?? []).map((option) => (
                    <option value={option.value} key={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.key === "summary" ? (
                <textarea
                  aria-label={field.label}
                  value={fieldValues[field.key] ?? ""}
                  onChange={(event) =>
                    setFieldValues((current) => ({
                      ...current,
                      [field.key]: event.target.value,
                    }))
                  }
                />
              ) : (
                <input
                  type={field.type === "date" ? "date" : "text"}
                  aria-label={field.label}
                  value={fieldValues[field.key] ?? ""}
                  onChange={(event) =>
                    setFieldValues((current) => ({
                      ...current,
                      [field.key]: event.target.value,
                    }))
                  }
                />
              )}
            </label>
          ))}
        </section>
      ) : null}
      <p className="review-action-help">
        <strong>修改了原始文字、语音或缺项？</strong>
        先让 Agent 根据修改重新整理；这一步不会写入系统。
      </p>
      <div className="review-actions">
        <button
          className="secondary-button"
          type="button"
          disabled={!sourceDirty || busy}
          onClick={reorganize}
        >
          根据修改重新整理
        </button>
        <button
          className="primary-button"
          type="button"
          disabled={missing.length > 0 || !confirmationId || busy}
          onClick={confirm}
        >
          确认并写入样品系统
        </button>
      </div>
      <button className="cancel-task-button" type="button" disabled={busy} onClick={onCancel}>
        取消任务
      </button>
    </div>
  );
}
